/**
 * 文件主要负责生成不同类型的文字message配置，包括普通文字，@文字，#文字，url文字，新增文字解析可直接在此处添加
 */

import { Component } from 'vue';
import { extractTextNodeList } from '@/business/chat/generate-text-node';
import { TextNode, TextNodeType } from '_c/typings/model/chat';
import { logger } from '_c/util/logger';

const TAG = 'GenerateTextNodeConfig';

export interface TextNodeConfigPropsDefault {
  displayText: string,
  innerText: string,
  text: string,
}

/**
 * 文字块基础结构
 */
export interface TextNodeConfig {
  // 类型
  type: TextNodeType,
  // 展示组件，默认为未知组件
  component: Component,
  // 组件内属性，默认为空对象
  props: Record<string, unknown> & TextNodeConfigPropsDefault,
  // 是否合法，默认为true
  isValid: boolean,
  // 是否是未知消息或者不合法消息，此类消息不响应事件处理
  isUnknown: boolean,
}

export type TextNodeConfigDefault = Pick<TextNodeConfig, 'type' | 'props' | 'isValid' | 'isUnknown'>;

export type TextNodeConfigOptional = Partial<TextNodeConfig> & {
  // 展示组件，默认为未知组件
  component: Component,
};


/**
 * 生成默认的消息配置
 * @param textNode
 */
const generateDefaultTextNodeConfig = (textNode: TextNode): TextNodeConfigDefault => ({
  type: TextNodeType.normal,
  props: {
    text: textNode.text,
    innerText: textNode.innerText,
    displayText: textNode.displayText,
  },
  isValid: true,
  isUnknown: false,
});

/**
 * 生成非法消息配置
 * @param textNode
 */
let generateInvalidTextNodeConfig: (textNode: TextNode) => TextNodeConfig;

/**
 * 生成未知消息配置
 * @param textNode
 */
let generateUnknownTextNodeConfig: (textNode: TextNode) => TextNodeConfig;

/**
 * 解析文字node配置列表
 * @param text
 */
function extractTextNodeConfigList(text: string): Array<TextNodeConfig> {
  const t = text;
  const textNodeConfigList: Array<TextNodeConfig> = [];
  const textNodeList = extractTextNodeList(t);
  textNodeList.forEach((item) => {
    textNodeConfigList.push(getTextNodeConfig(item));
  });
  return textNodeConfigList;
}

const setInvalidTextNodeConfig = (options:
TextNodeConfigOptional | ((textNode: TextNode) => TextNodeConfigOptional)): void => {
  generateInvalidTextNodeConfig = (textNode: TextNode) => {
    let opts: TextNodeConfigOptional;
    if (typeof options === 'function') {
      opts = options(textNode);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultTextNodeConfig(textNode),
      ...opts,
    };
  };
};


const setUnknownTextNodeConfig = (options:
TextNodeConfigOptional | ((textNode: TextNode) => TextNodeConfigOptional)): void => {
  generateUnknownTextNodeConfig = (textNode: TextNode) => {
    let opts: TextNodeConfigOptional;
    if (typeof options === 'function') {
      opts = options(textNode);
    } else {
      opts = { ...options };
    }
    return {
      ...generateDefaultTextNodeConfig(textNode),
      ...opts,
    };
  };
};

const textNodeGenerateFuncMap: Partial<Record<TextNodeType, (textNode: TextNode) => TextNodeConfig>> = {};

const setTextNodeGenerateFunc = (
  textNodeType: TextNodeType,
  options: TextNodeConfigOptional | ((textNode: TextNode) => TextNodeConfigOptional),
): void => {
  if (TextNodeType[textNodeType]) {
    textNodeGenerateFuncMap[textNodeType] = (textNode: TextNode) => {
      let opts: TextNodeConfigOptional;
      if (typeof options === 'function') {
        opts = options(textNode);
      } else {
        opts = { ...options };
      }
      return {
        ...generateDefaultTextNodeConfig(textNode),
        type: textNodeType,
        ...opts,
      };
    };
  }
};

const getTextNodeConfig = (textNode: TextNode): TextNodeConfig => {
  const fn = textNodeGenerateFuncMap[textNode.type];
  if (fn) {
    try {
      const textNodeConfig = fn.call(undefined, textNode);
      if (textNodeConfig.isValid) {
        return textNodeConfig;
      }
      return generateInvalidTextNodeConfig(textNode);
    } catch (err) {
      logger.error(TAG, 'generate text node config fail, err', err);
    }
  }
  if (typeof generateUnknownTextNodeConfig === 'function') {
    return generateUnknownTextNodeConfig(textNode);
  }
  logger.error(TAG, 'generate unknown config fail');
  throw `${TAG}, generate unknown config fail`;
};

export {
  extractTextNodeConfigList,
  setTextNodeGenerateFunc,
  setUnknownTextNodeConfig,
  setInvalidTextNodeConfig,
};
